// Client for the Search Term Performance endpoints.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/search-terms`;

const headers = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// Raised when the seller has no usable Amazon authorization, so the page can
// show the connect gate rather than a generic error.
export class NotConnectedError extends Error {
  constructor(message) {
    super(message || 'Amazon account not connected');
    this.notConnected = true;
  }
}

const call = async (url, options) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.connected === false) throw new NotConnectedError(data.error);
  if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, v);
  });
  return search.toString();
};


export const getTerm = (token, term, params) =>
  call(`${BASE}/term/${encodeURIComponent(term)}?${query(params)}`, { headers: headers(token) });

export const getTermTrend = (token, term, params) =>
  call(`${BASE}/term/${encodeURIComponent(term)}/trend?${query(params)}`,
       { headers: headers(token) });






// The marketplace's own Top Search Terms — the input behind "Not ranking" and
// "Competitor wins". One request covers every seller on the marketplace, so
// the rows are shared rather than collected per seller. The POST returns as
// soon as the report is requested; the collect poll finishes it.
export const getMarketStatus = (token, params) =>
  call(`${BASE}/market-terms?${query(params)}`, { headers: headers(token) });

export const startMarketPull = (token, body) =>
  call(`${BASE}/market-terms`, {
    method: 'POST', headers: headers(token), body: JSON.stringify(body || {}),
  });




// Sales & Traffic supplies the denominators attribution needs: a product's
// total revenue and total sessions. Not a Brand Analytics report.
export const startSalesTrafficPull = (token, body) =>
  call(`${BASE}/sales-traffic`, {
    method: 'POST', headers: headers(token), body: JSON.stringify(body || {}),
  });

export const getSettings = (token) =>
  call(`${BASE}/settings`, { headers: headers(token) });

export const saveSettings = (token, body) =>
  call(`${BASE}/settings`, { method: 'PUT', headers: headers(token), body: JSON.stringify(body) });

// SF3 seller correction. `label: null` returns the term to automatic classification.
export const reclassify = (token, searchTerm, label) =>
  call(`${BASE}/classification`, {
    method: 'PUT', headers: headers(token),
    body: JSON.stringify({ search_term: searchTerm, label }),
  });

export const startCollection = (token, body) =>
  call(`${BASE}/collect`, { method: 'POST', headers: headers(token), body: JSON.stringify(body) });

// Two-tier: the backend asks Amazon which products got impressions, then
// collects only those. One catalogue-wide request instead of one per product.
export const startAutoCollection = (token, { period, startDate, endDate } = {}) =>
  call(`${BASE}/collect`, {
    method: 'POST', headers: headers(token),
    body: JSON.stringify({
      auto: true, period,
      ...(startDate ? { start_date: startDate, end_date: endDate } : {}),
    }),
  });

export const getCollection = (token, params) =>
  call(`${BASE}/collect?${query(params)}`, { headers: headers(token) });

// Catalogue for the product picker — same endpoint the other pickers use.
export const getCatalogue = async (token) => {
  const res = await fetch(`${API_URL}/oauth_amazon/amazon/catalogue`, { headers: headers(token) });
  if (!res.ok) throw new Error(`Failed to load catalogue (${res.status})`);
  return (await res.json()).products || [];
};
